import { Injectable, inject } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DatabaseService, QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/** Full form definition including fields and layout settings. */
export interface FormDefinitionFull {
  fields: FormlyFieldConfig[];
  /** Number of equal-width columns for the rendered form. Defaults to 1. */
  columns: 1 | 2 | 3 | 4;
}

/**
 * Service for managing form definitions stored in the database
 */
@Injectable({
  providedIn: 'root',
})
export class FormDefinitionService {
  private db = inject(DatabaseService);

  private logger = inject(LoggerService).getLogger('forms');

  /**
   * Get a form definition by ID, including layout settings (columns).
   * Use this when rendering or pre-loading a user-facing form.
   */
  async getFormDefinitionFull(id: string): Promise<FormDefinitionFull | null> {
    await this.db.init();

    try {
      const results = await this.db.exec('SELECT data FROM form_definitions WHERE id = ?', [id]);

      if (results.length === 0) {
        this.logger.warn(`Form definition '${id}' not found`);
        return null;
      }

      const definition = JSON.parse(results[0].data as string);
      const fields = this.normalizeFieldKeys(definition.fields || []);
      const columns = ([1, 2, 3, 4].includes(definition.columns) ? definition.columns : 1) as
        | 1
        | 2
        | 3
        | 4;

      return { fields, columns };
    } catch (error) {
      this.logger.error(`Failed to load form definition '${id}':`, error);
      return null;
    }
  }

  /**
   * Get a form definition by ID
   */
  async getFormDefinition(id: string): Promise<FormlyFieldConfig[] | null> {
    await this.db.init();

    try {
      const results = await this.db.exec('SELECT data FROM form_definitions WHERE id = ?', [id]);

      if (results.length === 0) {
        this.logger.warn(`Form definition '${id}' not found`);
        return null;
      }

      const data = results[0].data;
      const definition = JSON.parse(data as string);

      this.logger.debug(`Loaded form definition '${id}':`, definition);

      // Return the fields array from the definition, remapping key → id so
      // the Formly form control key is always an opaque unique identifier
      // rather than a user-provided value (which may not be unique).
      return this.normalizeFieldKeys(definition.fields || []);
    } catch (error) {
      this.logger.error(`Failed to load form definition '${id}':`, error);
      return null;
    }
  }

  /**
   * Create a reactive query for a form definition by ID
   * Automatically refreshes when the form_definitions table changes
   */
  createQueryById(id: string): QuerySignal<FormlyFieldConfig[] | null> {
    return this.db.createQuery('form_definitions', () => this.getFormDefinition(id));
  }

  /**
   * Get a form definition by type (type is stored as the id in new schema)
   */
  async getFormDefinitionByType(type: string): Promise<FormlyFieldConfig[] | null> {
    await this.db.init();

    try {
      const results = await this.db.exec('SELECT id, data FROM form_definitions WHERE id = ?', [
        type,
      ]);

      if (results.length === 0) {
        this.logger.warn(`Form definition with type '${type}' not found`);
        return null;
      }

      const data = results[0].data;
      const definition = JSON.parse(data as string);

      this.logger.debug(`Loaded form definition by type '${type}':`, definition);

      return this.normalizeFieldKeys(definition.fields || []);
    } catch (error) {
      this.logger.error(`Failed to load form definition by type '${type}':`, error);
      return null;
    }
  }

  /**
   * Remap each field's Formly `key` to the field's stored `id` (nanoid/UUID)
   * so that the form control key is always an opaque, unique identifier.
   * Falls back to the original `key` for legacy fields that pre-date id storage.
   * This prevents user-provided human-readable keys from leaking into HTML
   * attributes and causing browser autofill cross-population between fields.
   */
  private normalizeFieldKeys(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return fields.map((field) => {
      const id = (field as any).id;
      if (!id) return field;
      return { ...field, key: id };
    });
  }

  /**
   * Save (create or update) a form definition.
   * Stores fields, optional name, and layout columns inside the JSON `data` column.
   * Uses the correct schema columns: id, data, created_by, created_at, updated_by, updated_at.
   */
  async saveFormDefinition(
    id: string,
    fields: FormlyFieldConfig[],
    name?: string,
    columns?: 1 | 2 | 3 | 4,
  ): Promise<string> {
    await this.db.init();

    const dataPayload: Record<string, unknown> = { fields };
    if (name) {
      dataPayload['name'] = name;
    }
    if (columns && columns !== 1) {
      // Only persist non-default values; absence ≡ 1 column when reading back.
      dataPayload['columns'] = columns;
    }
    const dataJson = JSON.stringify(dataPayload);
    const now = new Date().toISOString();

    try {
      // Check if form definition already exists
      const existing = await this.db.exec('SELECT id FROM form_definitions WHERE id = ?', [id]);

      if (existing.length > 0) {
        await this.db.run(
          `UPDATE form_definitions SET data = ?, updated_by = ?, updated_at = ? WHERE id = ?`,
          [dataJson, '', now, id],
        );
      } else {
        await this.db.run(
          `INSERT INTO form_definitions (id, data, created_by, created_at, updated_by, updated_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [id, dataJson, '', now, '', now],
        );
      }

      this.logger.debug(`Saved form definition '${id}'`);
      return id;
    } catch (error) {
      this.logger.error(`Failed to save form definition '${id}':`, error);
      throw error;
    }
  }

  /**
   * List all form definitions
   */
  async listFormDefinitions(): Promise<{ id: string; data: string }[]> {
    await this.db.init();

    try {
      const results = await this.db.exec('SELECT id, data FROM form_definitions ORDER BY id');

      return results.map((row) => ({
        id: row.id as string,
        data: row.data as string,
      }));
    } catch (error) {
      this.logger.error('Failed to list form definitions:', error);
      return [];
    }
  }
}
